import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Shield, Eye, EyeOff, Loader2, Lock, Terminal, Mail } from "lucide-react";
import { PortalSwitcher } from "@/components/portal-switcher";
import { ForgotPasswordModal } from "@/pages/auth/forgot-password-modal";

const BASE = import.meta.env.BASE_URL;

export default function MasterAdminLogin() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) navigate("/admin", { replace: true });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${BASE}api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("admin_token", data.token);
        localStorage.setItem("admin_user", JSON.stringify(data.member));
        navigate("/admin", { replace: true });
      } else {
        const err = await res.json();
        setError(err.error || "Access denied");
      }
    } catch {
      setError("Unable to connect. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center px-4 pt-0">
      <div className="fixed inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: "radial-gradient(circle at 50% 50%, #FF5C00 0%, transparent 50%)"
      }} />

      <nav className="relative w-full sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 py-2">
          <PortalSwitcher current="admin" tone="dark" />
        </div>
      </nav>

      <div className="relative w-full max-w-sm flex-1 flex flex-col justify-center py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] mb-4">
            <Terminal className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-serif text-white tracking-tight">Master Admin</h1>
          <p className="text-white/30 text-xs font-mono uppercase tracking-widest mt-2">Favcy Select</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          <div>
            <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Email</label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pl-10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                placeholder="you@favcy.in"
                required
                autoFocus
                autoComplete="email"
              />
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
            </div>
          </div>

          <div>
            <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pr-10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors font-mono"
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-2 text-right">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-xs text-white/40 hover:text-[#FF5C00] transition-colors"
              >
                Forgot password?
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full bg-gradient-to-r from-[#FF5C00] to-[#E55200] text-white py-3 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Authenticating...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Access Dashboard
              </>
            )}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-2 text-white/15 text-xs">
          <Shield className="w-3 h-3" />
          <span>Restricted Access — Favcy Internal Only</span>
        </div>
      </div>

      <ForgotPasswordModal
        audience="admin"
        open={forgotOpen}
        initialEmail={email}
        onClose={() => setForgotOpen(false)}
      />
    </div>
  );
}
